import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, Mail } from "lucide-react";
import { NAV_LINKS } from "../mock";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";
import NotificationBell from "./NotificationBell";

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="relative z-30 border-b border-white/10 bg-[#333]/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 md:px-8">
        <Link to="/" className="flex items-center gap-3">
          <Logo className="h-16 w-16 md:h-24 md:w-24" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-8 md:flex">
          {NAV_LINKS.map((link) =>
            link.label === "DONATE" ? (
              <Link
                key={link.to}
                to={link.to}
                data-testid="nav-donate-btn"
                className="rounded-full border-2 border-[#e0a82e] px-5 py-1.5 font-display text-lg font-600 uppercase tracking-wide text-[#e0a82e] transition-colors duration-200 hover:bg-[#e0a82e] hover:text-[#1c1c1c]"
              >
                {link.label}
              </Link>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                className={`font-display text-lg font-500 uppercase tracking-wide transition-colors duration-200 hover:text-[#e0a82e] ${
                  isActive(link.to) ? "text-[#e0a82e]" : "text-[#e0574c]"
                }`}
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden items-center gap-4 md:flex">
          {user ? (
            <>
              <NotificationBell />
              {user.is_admin && (
                <Link to="/admin/announce" title="Email announcement" data-testid="admin-announce-link" className="text-[#e0a82e] transition-colors hover:text-[#f2b93f]">
                  <Mail className="h-5 w-5" />
                </Link>
              )}
              <span className="font-display text-base uppercase text-[#e0a82e]">
                {user.username}
              </span>
              <button
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="flex items-center gap-1 font-display text-base uppercase text-[#e0574c] transition-colors hover:text-[#e0a82e]"
              >
                <LogOut className="h-4 w-4" /> Log Out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/register"
                className="rounded bg-[#e0a82e] px-5 py-2 font-display text-base font-600 uppercase tracking-wide text-[#1c1c1c] transition-colors duration-200 hover:bg-[#f2b93f]"
              >
                Register
              </Link>
              <Link
                to="/login"
                className="font-display text-base font-500 uppercase tracking-wide text-[#e0574c] underline-offset-4 transition-colors hover:text-[#e0a82e] hover:underline"
              >
                Log In
              </Link>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <div className="flex items-center gap-4 md:hidden">
          {user && <NotificationBell testid="notification-bell-mobile" />}
          <button
            className="text-[#e0a82e]"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-white/10 bg-[#2a2a2a] px-4 py-4 md:hidden">
          <nav className="flex flex-col gap-4">
            {NAV_LINKS.map((link) =>
              link.label === "DONATE" ? (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  data-testid="nav-donate-btn-mobile"
                  className="rounded-full border-2 border-[#e0a82e] px-5 py-2 text-center font-display text-lg font-600 uppercase text-[#e0a82e]"
                >
                  {link.label}
                </Link>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  className={`font-display text-lg uppercase ${
                    isActive(link.to) ? "text-[#e0a82e]" : "text-[#e0574c]"
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
            <div className="mt-2 flex flex-col gap-3 border-t border-white/10 pt-4">
              {user ? (
                <>
                {user.is_admin && (
                  <Link to="/admin/announce" onClick={() => setOpen(false)} data-testid="admin-announce-link-mobile" className="font-display text-lg uppercase text-[#e0a82e]">
                    Email Announcement
                  </Link>
                )}
                <button
                  onClick={() => {
                    logout();
                    setOpen(false);
                    navigate("/");
                  }}
                  className="text-left font-display text-lg uppercase text-[#e0574c]"
                >
                  Log Out ({user.username})
                </button>
                </>
              ) : (
                <>
                  <Link
                    to="/register"
                    onClick={() => setOpen(false)}
                    className="rounded bg-[#e0a82e] px-5 py-2 text-center font-display text-base uppercase text-[#1c1c1c]"
                  >
                    Register
                  </Link>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    className="font-display text-lg uppercase text-[#e0574c]"
                  >
                    Log In
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
